import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import PropTypes from "prop-types";
import {
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import {useVirtualizer} from "@tanstack/react-virtual";
import {useTranslation} from "react-i18next";
import {
    DndContext,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
    restrictToFirstScrollableAncestor,
    restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import {DragDropContext, Draggable, Droppable} from "react-beautiful-dnd";
import {CSS} from "@dnd-kit/utilities";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader as PrimitiveTableHeader,
    TableRow,
} from "./table";
import {useColumnSizingManager} from "./use-column-sizing-manager";
import Loader from "../common/loader/loader";
import FormPopup from "../common/popup/form-popup";
import EmptyState, {NO_DATA} from "../states/empty-state";
import {classNames} from "../../utils/Utils";
import DragIcon from "../icons/drag-icon";
import {
    ArrowDownIcon,
    ArrowUpIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    PlusIcon,
    SwitchVerticalIcon,
    ViewListIcon,
    XIcon,
} from "@heroicons/react/outline";
import InputSearch from "../common/form/input-search";

const normalizeOffset = (value) => {
    if (value === undefined || value === null) {
        return undefined;
    }
    return typeof value === "number" ? `${value}px` : value;
};

const PINNED_CELL_Z_INDEX = 5;
const PINNED_HEADER_Z_INDEX = 7;

const getCommonPinningStyles = (column, options = {}) => {
    if (!column) {
        return {};
    }
    const {isHeader = false} = options;
    const isPinned = column.getIsPinned();
    const isLastLeftPinnedColumn =
        isPinned === "left" && column.getIsLastColumn("left");
    const isFirstRightPinnedColumn =
        isPinned === "right" && column.getIsFirstColumn("right");
    const columnLeftOverride =
        column.columnDef?.left ?? column.columnDef?.meta?.left;
    const columnRightOverride =
        column.columnDef?.right ?? column.columnDef?.meta?.right;
    const columnDef = column.columnDef ?? {};
    const size = column.getSize();
    const minWidth =
        columnDef.meta?.minWidth ??
        (typeof columnDef.minSize === "number" ? columnDef.minSize : size);
    const maxWidth =
        columnDef.meta?.maxWidth ??
        (typeof columnDef.maxSize === "number" ? columnDef.maxSize : undefined);

    return {
        boxShadow: isLastLeftPinnedColumn
            ? "-3px 0 3px -4px gray inset"
            : isFirstRightPinnedColumn
                ? "3px 0 3px -4px gray inset"
                : undefined,
        paddingRight: isFirstRightPinnedColumn ? "20px" : undefined,
        left:
            normalizeOffset(columnLeftOverride) ??
            (isPinned === "left" ? `${column.getStart("left")}px` : undefined),
        right:
            normalizeOffset(columnRightOverride) ??
            (isPinned === "right"
                ? `${column.getAfter("right")}px`
                : undefined),
        position: isPinned ? "sticky" : undefined,
        width: size,
        minWidth,
        ...(typeof maxWidth === "number" ? {maxWidth} : {}),
        zIndex: isPinned
            ? (isHeader ? PINNED_HEADER_Z_INDEX : PINNED_CELL_Z_INDEX)
            : undefined,
    };
};

const isBrowser = typeof window !== "undefined";
const DESKTOP_MEDIA_QUERY = "(min-width: 1024px)";

const resolveIsDesktop = () => {
    if (!isBrowser || typeof window.matchMedia !== "function") {
        return false;
    }
    return window.matchMedia(DESKTOP_MEDIA_QUERY).matches;
};

const buildLocalStorageKey = (listKey, suffix) => {
    if (!listKey) {
        return null;
    }
    return `editable-table:${listKey}:${suffix}`;
};

const loadFromStorage = (key, fallback) => {
    if (!key || !isBrowser) {
        return fallback;
    }
    try {
        const stored = window.localStorage.getItem(key);
        return stored ? JSON.parse(stored) : fallback;
    } catch (error) {
        return fallback;
    }
};

const saveToStorage = (key, value) => {
    if (!key || !isBrowser) {
        return;
    }
    try {
        window.localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
        // ignore storage errors silently
    }
};

const reorderArray = (list, startIndex, endIndex) => {
    const result = Array.from(list ?? []);
    const [removed] = result.splice(startIndex, 1);
    if (removed === undefined) {
        return result;
    }
    result.splice(endIndex, 0, removed);
    return result;
};

const mergeColumnOrder = (baseOrder, nextOrder) => {
    const base = Array.isArray(baseOrder) ? baseOrder.map((id) => String(id)) : [];
    if (base.length === 0) {
        return [];
    }
    const seen = new Set();
    const sanitizedNext = Array.isArray(nextOrder)
        ? nextOrder
            .map((id) => String(id))
            .filter((id) => base.includes(id))
            .filter((id) => {
                if (seen.has(id)) {
                    return false;
                }
                seen.add(id);
                return true;
            })
        : [];
    const missing = base.filter((id) => !seen.has(id));
    return [...sanitizedNext, ...missing];
};

const arraysEqual = (a, b) => {
    if (a === b) {
        return true;
    }
    if (!Array.isArray(a) || !Array.isArray(b)) {
        return false;
    }
    if (a.length !== b.length) {
        return false;
    }
    for (let index = 0; index < a.length; index += 1) {
        if (a[index] !== b[index]) {
            return false;
        }
    }
    return true;
};

const deriveRowStyling = (resolveRowStyling, row) => {
    if (typeof resolveRowStyling !== "function") {
        return {};
    }
    const styling = resolveRowStyling(row);
    if (!styling || typeof styling !== "object") {
        return {};
    }
    return styling;
};

const DataTableEditable = ({
    listKey,
    columns,
    data,
    isLoading,
    searchValue,
    onSearchChange,
    searchResetToken,
    searchPlaceholder,
    enableSearch,
    enableColumnVisibility,
    enableColumnReorder,
    initialColumnVisibility,
    enableReorder,
    onReorder,
    onSorting,
    onAddRow,
    addButtonLabel,
    toolbarActions,
    emptyStateTitle,
    emptyStateDescription,
    className,
    fixedHeight,
    enableScroll,
    leftPinning,
    rightPinning,
    stickyHeader,
    stickyHeaderOffset,
    stickyHeaderZIndex,
    isRowSelected,
    resolveRowStyling,
    virtualizeRows,
    virtualRowEstimateSize,
    virtualOverscan,
}) => {
    const {t} = useTranslation();

    const columnVisibilityStorageKey = useMemo(
        () => buildLocalStorageKey(listKey, "column-visibility"),
        [listKey]
    );
    const columnSizingStorageKey = useMemo(
        () => buildLocalStorageKey(listKey, "column-sizing"),
        [listKey]
    );
    const columnOrderStorageKey = useMemo(
        () => (enableColumnReorder ? buildLocalStorageKey(listKey, "column-order") : null),
        [enableColumnReorder, listKey]
    );

    const [columnVisibility, setColumnVisibility] = useState(() => {
        const stored = loadFromStorage(columnVisibilityStorageKey, null);
        if (stored) {
            return stored;
        }
        return initialColumnVisibility ?? {};
    });

    const initialColumnSizing = useMemo(
        () => (columnSizingStorageKey ? loadFromStorage(columnSizingStorageKey, {}) : {}),
        [columnSizingStorageKey]
    );

    const {
        columnSizing,
        setColumnSizing: handleColumnSizingChange,
        replaceColumnSizing,
        flushPendingSizing: flushPendingColumnSizing,
    } = useColumnSizingManager(initialColumnSizing);

    useEffect(() => {
        replaceColumnSizing(initialColumnSizing);
    }, [initialColumnSizing, replaceColumnSizing]);

    const lastSavedColumnSizingRef = useRef({
        key: columnSizingStorageKey,
        value: columnSizing,
    });
    const defaultColumnOrder = useMemo(() => {
        const ids = [];
        const visit = (definitions) => {
            (definitions ?? []).forEach((definition) => {
                if (!definition) {
                    return;
                }
                if (Array.isArray(definition.columns)) {
                    visit(definition.columns);
                    return;
                }
                const resolvedId = definition.id ?? definition.accessorKey;
                if (resolvedId === undefined || resolvedId === null) {
                    return;
                }
                const stringId = String(resolvedId);
                if (!ids.includes(stringId)) {
                    ids.push(stringId);
                }
            });
        };
        visit(columns ?? []);
        return ids;
    }, [columns]);
    const [columnOrder, setColumnOrder] = useState(defaultColumnOrder);
    const columnOrderLoadedRef = useRef(false);
    const [sorting, setSorting] = useState([]);
    const previousSortingRef = useRef([]);
    const [showColumnMenu, setShowColumnMenu] = useState(false);
    const [isColumnReorderOpen, setIsColumnReorderOpen] = useState(false);
    const columnMenuRef = useRef(null);
    const toolbarRef = useRef(null);
    const scrollContainerRef = useRef(null);
    const measurementFrameRef = useRef(null);
    const pendingMeasurementNodesRef = useRef(new Set());
    const isDraggingRef = useRef(false);
    const pointerPositionRef = useRef({x: null, y: null});
    const initialPointerRef = useRef({x: null, y: null});
    const autoScrollFrameRef = useRef(null);
    const [horizontalScrollState, setHorizontalScrollState] = useState({
        hasOverflow: false,
        canScrollLeft: false,
        canScrollRight: false,
    });
    const [isDesktop, setIsDesktop] = useState(resolveIsDesktop);
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {distance: 6},
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    useEffect(() => {
        if (!isBrowser || typeof window.matchMedia !== "function") {
            return undefined;
        }
        const mediaQueryList = window.matchMedia(DESKTOP_MEDIA_QUERY);
        const handleChange = (event) => setIsDesktop(event.matches);
        setIsDesktop(mediaQueryList.matches);
        if (typeof mediaQueryList.addEventListener === "function") {
            mediaQueryList.addEventListener("change", handleChange);
            return () => mediaQueryList.removeEventListener("change", handleChange);
        }
        if (typeof mediaQueryList.addListener === "function") {
            mediaQueryList.addListener(handleChange);
            return () => mediaQueryList.removeListener(handleChange);
        }
        return undefined;
    }, []);

    const runAutoScroll = useCallback(() => {
        const container = scrollContainerRef.current;
        if (!container || !isDraggingRef.current) {
            autoScrollFrameRef.current = null;
            return;
        }

        const pointerY = pointerPositionRef.current.y;
        if (pointerY === null || pointerY === undefined) {
            autoScrollFrameRef.current = window.requestAnimationFrame(runAutoScroll);
            return;
        }

        const rect = container.getBoundingClientRect();
        const threshold = 72;
        const bottomOffset = rect.bottom - pointerY;
        const topOffset = pointerY - rect.top;
        let scrollDelta = 0;

        const maxScrollTop = container.scrollHeight - container.clientHeight;
        const canScrollDown = container.scrollTop < maxScrollTop;
        const canScrollUp = container.scrollTop > 0;

        if (bottomOffset < threshold && bottomOffset > 0 && canScrollDown) {
            const intensity = (threshold - bottomOffset) / threshold;
            scrollDelta = Math.min(24, Math.max(6, intensity * 24));
        } else if (topOffset < threshold && topOffset > 0 && canScrollUp) {
            const intensity = (threshold - topOffset) / threshold;
            scrollDelta = -Math.min(24, Math.max(6, intensity * 24));
        }

        if (scrollDelta !== 0) {
            container.scrollTop += scrollDelta;
        }

        autoScrollFrameRef.current = window.requestAnimationFrame(runAutoScroll);
    }, []);

    const startAutoScroll = useCallback(() => {
        if (!enableReorder || !isBrowser || autoScrollFrameRef.current !== null) {
            return;
        }
        autoScrollFrameRef.current = window.requestAnimationFrame(runAutoScroll);
    }, [enableReorder, runAutoScroll]);

    const stopAutoScroll = useCallback(() => {
        isDraggingRef.current = false;
        pointerPositionRef.current = {x: null, y: null};
        initialPointerRef.current = {x: null, y: null};
        if (autoScrollFrameRef.current !== null && isBrowser) {
            window.cancelAnimationFrame(autoScrollFrameRef.current);
            autoScrollFrameRef.current = null;
        }
    }, []);

    const updateHorizontalScrollState = useCallback(() => {
        const container = scrollContainerRef.current;
        if (!container) {
            setHorizontalScrollState((previous) => {
                if (!previous.hasOverflow && !previous.canScrollLeft && !previous.canScrollRight) {
                    return previous;
                }
                return {
                    hasOverflow: false,
                    canScrollLeft: false,
                    canScrollRight: false,
                };
            });
            return;
        }
        const scrollWidth = container.scrollWidth ?? 0;
        const clientWidth = container.clientWidth ?? 0;
        const maxScrollLeft = Math.max(0, scrollWidth - clientWidth);
        const hasOverflow = maxScrollLeft > 1;
        const currentScrollLeft = container.scrollLeft ?? 0;
        const distanceToEnd = Math.max(0, maxScrollLeft - currentScrollLeft);
        const canScrollLeft = hasOverflow && currentScrollLeft > 1;
        const canScrollRight = hasOverflow && distanceToEnd > 1;
        setHorizontalScrollState((previous) => {
            if (
                previous.hasOverflow === hasOverflow
                && previous.canScrollLeft === canScrollLeft
                && previous.canScrollRight === canScrollRight
            ) {
                return previous;
            }
            return {
                hasOverflow,
                canScrollLeft,
                canScrollRight,
            };
        });
    }, []);

    const handleHorizontalScroll = useCallback((direction) => {
        if (direction === 0) {
            return;
        }
        const container = scrollContainerRef.current;
        if (!container) {
            return;
        }
        const baseDistance = container.clientWidth > 0 ? container.clientWidth * 0.6 : 240;
        container.scrollBy({
            left: direction * baseDistance,
            behavior: "smooth",
        });
    }, []);

    const initialPinning = useMemo(() => {
        const derivedLeft = [];
        const derivedRight = [];
        (columns ?? []).forEach((column) => {
            if (!column) {
                return;
            }
            const columnId = column.id ?? column.accessorKey;
            if (!columnId) {
                return;
            }
            const pin = column.meta?.pin ?? column.meta?.sticky;
            if (pin === "left") {
                derivedLeft.push(columnId);
            }
            if (pin === "right") {
                derivedRight.push(columnId);
            }
        });
        return {
            left: (leftPinning && leftPinning.length) ? leftPinning : derivedLeft,
            right: (rightPinning && rightPinning.length) ? rightPinning : derivedRight,
        };
    }, [columns, leftPinning, rightPinning]);

    const [columnPinning, setColumnPinning] = useState(initialPinning);

    useEffect(() => {
        if (initialColumnVisibility) {
            setColumnVisibility((prev) => ({...initialColumnVisibility, ...prev}));
        }
    }, [initialColumnVisibility]);

    useEffect(() => {
        saveToStorage(columnVisibilityStorageKey, columnVisibility);
    }, [columnVisibility, columnVisibilityStorageKey]);

    useEffect(() => {
        updateHorizontalScrollState();
        const container = scrollContainerRef.current;
        if (!container) {
            return undefined;
        }
        const handleScroll = () => {
            updateHorizontalScrollState();
        };
        container.addEventListener("scroll", handleScroll, {passive: true});
        return () => {
            container.removeEventListener("scroll", handleScroll);
        };
    }, [updateHorizontalScrollState]);

    useEffect(() => {
        if (!isBrowser) {
            return undefined;
        }
        let animationFrameId = null;
        const handleResize = () => {
            if (animationFrameId !== null) {
                return;
            }
            animationFrameId = window.requestAnimationFrame(() => {
                animationFrameId = null;
                updateHorizontalScrollState();
            });
        };
        const flushResize = () => {
            if (animationFrameId !== null) {
                window.cancelAnimationFrame(animationFrameId);
                animationFrameId = null;
            }
            updateHorizontalScrollState();
        };
        window.addEventListener("resize", handleResize);
        const container = scrollContainerRef.current;
        const ResizeObserverConstructor = window.ResizeObserver;
        let observer = null;
        if (container && typeof ResizeObserverConstructor === "function") {
            observer = new ResizeObserverConstructor(handleResize);
            observer.observe(container);
            const tableElement = container.firstElementChild;
            if (tableElement) {
                observer.observe(tableElement);
            }
        }
        flushResize();
        return () => {
            window.removeEventListener("resize", handleResize);
            if (animationFrameId !== null) {
                window.cancelAnimationFrame(animationFrameId);
            }
            if (observer) {
                observer.disconnect();
            }
        };
    }, [updateHorizontalScrollState]);

    useEffect(() => {
        if (!enableColumnReorder) {
            columnOrderLoadedRef.current = false;
            return;
        }
        if (columnOrderLoadedRef.current) {
            return;
        }
        if (!columnOrderStorageKey) {
            columnOrderLoadedRef.current = true;
            return;
        }
        const storedOrder = loadFromStorage(columnOrderStorageKey, null);
        if (Array.isArray(storedOrder) && storedOrder.length > 0) {
            setColumnOrder((prev) => mergeColumnOrder(defaultColumnOrder, storedOrder ?? prev));
        }
        columnOrderLoadedRef.current = true;
    }, [enableColumnReorder, columnOrderStorageKey, defaultColumnOrder]);

    useEffect(() => {
        setColumnOrder((prev) => {
            const nextOrder = mergeColumnOrder(defaultColumnOrder, prev);
            return arraysEqual(prev, nextOrder) ? prev : nextOrder;
        });
    }, [defaultColumnOrder]);

    useEffect(() => {
        if (!enableColumnReorder) {
            return;
        }
        if (!columnOrderStorageKey) {
            return;
        }
        if (!columnOrderLoadedRef.current) {
            return;
        }
        if (!Array.isArray(columnOrder) || columnOrder.length === 0) {
            return;
        }
        saveToStorage(columnOrderStorageKey, columnOrder);
    }, [columnOrder, enableColumnReorder, columnOrderStorageKey]);

    useEffect(() => {
        setColumnPinning(initialPinning);
    }, [initialPinning]);

    useEffect(() => {
        if (!showColumnMenu) {
            return undefined;
        }
        const handleInteractionOutside = (event) => {
            if (columnMenuRef.current && !columnMenuRef.current.contains(event.target)) {
                setShowColumnMenu(false);
            }
        };
        const events = ["mousedown", "touchstart"];
        events.forEach((eventName) => document.addEventListener(eventName, handleInteractionOutside));
        return () => {
            events.forEach((eventName) => document.removeEventListener(eventName, handleInteractionOutside));
        };
    }, [showColumnMenu]);

    useEffect(() => {
        if (!enableReorder || !isBrowser) {
            return undefined;
        }
        const handlePointerMove = (event) => {
            if (!isDraggingRef.current) {
                return;
            }
            pointerPositionRef.current = {
                x: event.clientX,
                y: event.clientY,
            };
        };
        window.addEventListener("pointermove", handlePointerMove);
        return () => {
            window.removeEventListener("pointermove", handlePointerMove);
        };
    }, [enableReorder]);

    useEffect(() => () => {
        stopAutoScroll();
    }, [stopAutoScroll]);

    const handleColumnOrderChange = useCallback((nextOrder) => {
        if (!enableColumnReorder) {
            return;
        }
        setColumnOrder(mergeColumnOrder(defaultColumnOrder, nextOrder));
    }, [enableColumnReorder, defaultColumnOrder]);

    const table = useReactTable({
        data,
        columns,
        state: {
            sorting,
            columnVisibility,
            columnSizing,
            columnPinning,
            ...(enableColumnReorder ? {columnOrder} : {}),
        },
        manualSorting: Boolean(onSorting),
        onSortingChange: setSorting,
        onColumnVisibilityChange: setColumnVisibility,
        onColumnSizingChange: handleColumnSizingChange,
        onColumnPinningChange: setColumnPinning,
        onColumnOrderChange: enableColumnReorder ? handleColumnOrderChange : undefined,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        enableColumnResizing: true,
        columnResizeMode: "onChange",
        columnResizeDirection: "ltr",
    });
    const columnSizingInfo = table.getState().columnSizingInfo;
    const isColumnCurrentlyResizing = Boolean(columnSizingInfo?.isResizingColumn);

    useEffect(() => {
        if (!isColumnCurrentlyResizing) {
            flushPendingColumnSizing();
        }
    }, [isColumnCurrentlyResizing, flushPendingColumnSizing]);

    useEffect(() => {
        if (!columnSizingStorageKey) {
            lastSavedColumnSizingRef.current = {
                key: columnSizingStorageKey,
                value: columnSizing,
            };
            return;
        }
        if (isColumnCurrentlyResizing) {
            return;
        }
        const {key: previousKey, value: previousValue} = lastSavedColumnSizingRef.current;
        if (previousKey === columnSizingStorageKey && previousValue === columnSizing) {
            return;
        }
        saveToStorage(columnSizingStorageKey, columnSizing);
        lastSavedColumnSizingRef.current = {
            key: columnSizingStorageKey,
            value: columnSizing,
        };
    }, [columnSizing, columnSizingStorageKey, isColumnCurrentlyResizing]);

    useEffect(() => {
        if (typeof onSorting !== "function") {
            return;
        }
        const previous = previousSortingRef.current;
        previousSortingRef.current = sorting;

        if (!Array.isArray(sorting) || sorting.length === 0) {
            if (!Array.isArray(previous) || previous.length === 0) {
                return;
            }
            onSorting(null);
            return;
        }

        const [first] = sorting;
        if (!first || first.id === undefined || first.id === null) {
            return;
        }

        const column = table?.getColumn(first.id);
        const sortKeyRaw = column?.columnDef?.meta?.sortKey ?? first.id;
        if (sortKeyRaw === undefined || sortKeyRaw === null) {
            return;
        }

        onSorting({
            columnId: String(first.id),
            sortKey: String(sortKeyRaw),
            sortDir: first.desc ? "DESC" : "ASC",
        });
    }, [sorting, onSorting, table]);

    useEffect(() => {
        if (!enableColumnReorder) {
            return;
        }
        const leafColumns = table.getAllLeafColumns();
        if (!leafColumns || leafColumns.length === 0) {
            return;
        }
        const ids = leafColumns.map((column) => String(column.id));
        setColumnOrder((prev) => {
            const nextOrder = mergeColumnOrder(ids, prev);
            return arraysEqual(prev, nextOrder) ? prev : nextOrder;
        });
    }, [enableColumnReorder, table]);

    const resolveColumnLabel = useCallback((column) => {
        const headerDef = column.columnDef?.header;
        if (typeof headerDef === "string") {
            return headerDef;
        }
        if (typeof column.columnDef?.title === "string") {
            return column.columnDef.title;
        }
        if (typeof column.columnDef?.meta?.title === "string") {
            return column.columnDef.meta.title;
        }
        return column.id;
    }, []);

    const columnReorderItems = useMemo(() => {
        if (!enableColumnReorder) {
            return [];
        }
        const leafColumns = table.getAllLeafColumns();
        if (!leafColumns || leafColumns.length === 0) {
            return [];
        }
        const columnMap = new Map();
        leafColumns.forEach((column) => {
            if (!column) {
                return;
            }
            if (String(column.id) === "actions") {
                return;
            }
            const disableReorder = column.columnDef?.meta?.disableColumnReorder === true;
            if (disableReorder) {
                return;
            }
            columnMap.set(String(column.id), column);
        });
        const orderedIds = mergeColumnOrder(Array.from(columnMap.keys()), columnOrder);
        const visibilityState = columnVisibility ?? {};
        return orderedIds
            .map((columnId) => {
                const column = columnMap.get(columnId);
                if (!column) {
                    return null;
                }
                const visibilityKey = String(column.id);
                const isVisible = Object.prototype.hasOwnProperty.call(visibilityState, visibilityKey)
                    ? visibilityState[visibilityKey] !== false
                    : column.getIsVisible();
                return {
                    id: column.id,
                    label: resolveColumnLabel(column),
                    isVisible,
                };
            })
            .filter(Boolean);
    }, [enableColumnReorder, table, columnOrder, resolveColumnLabel, columnVisibility]);

    const reorderableColumnIds = useMemo(
        () => columnReorderItems.map((item) => String(item.id)),
        [columnReorderItems]
    );

    const handleOpenColumnReorder = useCallback(() => {
        setShowColumnMenu(false);
        setIsColumnReorderOpen(true);
    }, []);

    const handleColumnReorderDragEnd = useCallback((result) => {
        if (!enableColumnReorder) {
            return;
        }
        if (!result.destination || result.destination.index === result.source.index) {
            return;
        }
        if (reorderableColumnIds.length <= 1) {
            return;
        }
        setColumnOrder((prev) => {
            const base = mergeColumnOrder(defaultColumnOrder, prev);
            const reorderableSet = new Set(reorderableColumnIds);
            const currentOrder = base.filter((id) => reorderableSet.has(String(id)));
            if (currentOrder.length <= 1) {
                return base;
            }
            const nextOrderSubset = reorderArray(currentOrder, result.source.index, result.destination.index);
            let subsetIndex = 0;
            const nextBase = base.map((id) => {
                if (!reorderableSet.has(String(id))) {
                    return id;
                }
                const nextId = nextOrderSubset[subsetIndex];
                subsetIndex += 1;
                return nextId;
            });
            return arraysEqual(prev, nextBase) ? prev : nextBase;
        });
    }, [enableColumnReorder, defaultColumnOrder, reorderableColumnIds]);

    const headerGroups = table.getHeaderGroups();
    const rowModel = table.getRowModel();
    const rows = rowModel?.rows ?? [];
    const visibleColumns = table.getAllLeafColumns();
    const visibleColumnSignature = useMemo(
        () => visibleColumns.map((column) => {
            if (!column) {
                return "";
            }
            const columnId = column.id ?? column.accessorKey;
            return columnId ? String(columnId) : "";
        }).join("|"),
        [visibleColumns]
    );
    const dataLength = Array.isArray(data) ? data.length : 0;
    const resolveRowId = useCallback((row) => String(row.original?.id ?? row.id), []);
    const sortableRowIds = rows.map(resolveRowId);
    const shouldVirtualizeRows = useMemo(
        () => Boolean(virtualizeRows && enableScroll !== false && !enableReorder),
        [enableReorder, enableScroll, virtualizeRows]
    );
    const estimateRowSize = useCallback(() => virtualRowEstimateSize, [virtualRowEstimateSize]);
    const rowVirtualizer = useVirtualizer({
        count: shouldVirtualizeRows ? rows.length : 0,
        getScrollElement: () => scrollContainerRef.current,
        overscan: virtualOverscan,
        estimateSize: estimateRowSize,
        getItemKey: (index) => {
            const targetRow = rows[index];
            return targetRow ? resolveRowId(targetRow) : index;
        },
    });
    const virtualItems = shouldVirtualizeRows ? rowVirtualizer.getVirtualItems() : [];
    const virtualPaddingStart = shouldVirtualizeRows && virtualItems.length > 0 ? virtualItems[0].start : 0;
    const virtualPaddingEnd = shouldVirtualizeRows && virtualItems.length > 0
        ? rowVirtualizer.getTotalSize() - virtualItems[virtualItems.length - 1].end
        : 0;

    const flushPendingMeasurements = useCallback(() => {
        if (measurementFrameRef.current !== null && isBrowser) {
            window.cancelAnimationFrame(measurementFrameRef.current);
            measurementFrameRef.current = null;
        }
        if (pendingMeasurementNodesRef.current.size === 0) {
            return;
        }
        const nodes = Array.from(pendingMeasurementNodesRef.current);
        pendingMeasurementNodesRef.current.clear();
        nodes.forEach((target) => {
            if (target && target.isConnected) {
                rowVirtualizer.measureElement(target);
            }
        });
    }, [rowVirtualizer]);

    const scheduleRowMeasurement = useCallback((node) => {
        if (!node || !shouldVirtualizeRows || !isBrowser) {
            rowVirtualizer.measureElement(node);
            return;
        }
        pendingMeasurementNodesRef.current.add(node);
        if (measurementFrameRef.current !== null) {
            return;
        }
        measurementFrameRef.current = window.requestAnimationFrame(() => {
            measurementFrameRef.current = null;
            if (pendingMeasurementNodesRef.current.size === 0) {
                return;
            }
            const nodes = Array.from(pendingMeasurementNodesRef.current);
            pendingMeasurementNodesRef.current.clear();
            nodes.forEach((target) => {
                if (target && target.isConnected) {
                    rowVirtualizer.measureElement(target);
                }
            });
        });
    }, [rowVirtualizer, shouldVirtualizeRows]);

    useEffect(() => {
        if (!shouldVirtualizeRows) {
            flushPendingMeasurements();
        }
    }, [flushPendingMeasurements, shouldVirtualizeRows]);

    useEffect(() => () => {
        flushPendingMeasurements();
    }, [flushPendingMeasurements]);

    const handleDragStart = (event) => {
        if (!enableReorder) {
            return;
        }
        const activatorEvent = event?.activatorEvent;
        if (activatorEvent && "clientX" in activatorEvent && "clientY" in activatorEvent) {
            const {clientX, clientY} = activatorEvent;
            initialPointerRef.current = {x: clientX, y: clientY};
            pointerPositionRef.current = {x: clientX, y: clientY};
        } else {
            initialPointerRef.current = {x: null, y: null};
            pointerPositionRef.current = {x: null, y: null};
        }
        isDraggingRef.current = true;
        startAutoScroll();
    };

    const handleDragMove = (event) => {
        if (!enableReorder) {
            return;
        }
        const initial = initialPointerRef.current;
        if (initial.x !== null && initial.y !== null) {
            pointerPositionRef.current = {
                x: initial.x + (event?.delta?.x ?? 0),
                y: initial.y + (event?.delta?.y ?? 0),
            };
        }
    };

    const handleDragCancel = () => {
        stopAutoScroll();
    };

    const handleDragEnd = (event) => {
        stopAutoScroll();
        if (!enableReorder || !onReorder) {
            return;
        }
        const {active, over} = event ?? {};
        if (!active || !over || active.id === over.id) {
            return;
        }
        const stringActiveId = String(active.id);
        const stringOverId = String(over.id);
        const sourceIndex = rows.findIndex((row) => resolveRowId(row) === stringActiveId);
        const destinationIndex = rows.findIndex((row) => resolveRowId(row) === stringOverId);
        if (sourceIndex === -1 || destinationIndex === -1) {
            return;
        }
        const sourceRow = rows[sourceIndex];
        const destinationRow = rows[destinationIndex];
        if (!sourceRow || !destinationRow) {
            return;
        }
        onReorder({
            sourceId: sourceRow.original?.id,
            destinationId: destinationRow.original?.id,
            sourceIndex,
            destinationIndex,
        });
    };

    const renderCellContent = (cell, dragHandleProps) => {
        const content = flexRender(cell.column.columnDef.cell, cell.getContext());
        const isDragHandle = Boolean(cell.column.columnDef.meta?.dragHandle);
        if (isDragHandle && dragHandleProps) {
            const {listeners = {}, attributes = {}} = dragHandleProps;
            return (
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        aria-label={t("app.common.reorder", {defaultValue: "Reorder"})}
                        {...attributes}
                        {...listeners}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md  text-gray-900 bg-transparent "
                        data-drag-handle="true"
                    >
                        <DragIcon className="h-4 w-4" />
                    </button>
                    {content}
                </div>
            );
        }
        return content;
    };

    const SortableTableRow = ({row, rowId}) => {
        const rowIdentifier = rowId ?? resolveRowId(row);
        const {
            attributes,
            listeners,
            setNodeRef,
            transform,
            transition,
            isDragging,
        } = useSortable({id: rowIdentifier});
        const rowStyling = deriveRowStyling(resolveRowStyling, row);
        const {
            rowClassName: customRowClassName,
            cellClassName: customCellClassName,
            pinnedCellClassName: customPinnedCellClassName,
            cellHoverClassName: customCellHoverClassName,
            selectedCellClassName: customSelectedCellClassName,
        } = rowStyling;
        const rowStyle = {};
        if (transform) {
            rowStyle.transform = CSS.Transform.toString(transform);
        }
        if (transition) {
            rowStyle.transition = transition;
        }
        if (isDragging) {
            rowStyle.zIndex = 5;
            rowStyle.position = "relative";
        }
        const isSelected = isRowSelected(row);
        const dragHandleProps = {attributes, listeners};

        return (
            <TableRow
                ref={setNodeRef}
                style={rowStyle}
                className={classNames(
                    "group border-b border-gray-100",
                    isDragging ? "bg-blue-50 shadow" : "bg-white",
                    customRowClassName
                )}
                data-row-id={rowIdentifier}
            >
                {row.getVisibleCells().map((cell) => {
                    const isPinned = cell.column.getIsPinned();
                    const pinnedHighlightClass = customPinnedCellClassName ?? customCellClassName;
                    const defaultPinnedBackground = isPinned && !isSelected && !pinnedHighlightClass;
                    return (
                        <TableCell
                            key={cell.id}
                            style={{...getCommonPinningStyles(cell.column)}}
                            className={classNames(
                                "align-middle p-0 text-sm text-gray-700 transition-colors",
                                customCellHoverClassName ?? "group-hover:bg-gray-50",
                                customCellClassName,
                                isPinned && !isSelected && pinnedHighlightClass,
                                defaultPinnedBackground && "bg-white",
                                isSelected && (customSelectedCellClassName ?? "bg-gray-200")
                            )}
                        >
                            {renderCellContent(
                                cell,
                                cell.column.columnDef.meta?.dragHandle ? dragHandleProps : null
                            )}
                        </TableCell>
                    );
                })}
            </TableRow>
        );
    };

    const renderStaticRow = (row, options = {}) => {
        if (!row) {
            return null;
        }
        const rowStyling = deriveRowStyling(resolveRowStyling, row);
        const {
            rowClassName: customRowClassName,
            cellClassName: customCellClassName,
            pinnedCellClassName: customPinnedCellClassName,
            cellHoverClassName: customCellHoverClassName,
            selectedCellClassName: customSelectedCellClassName,
        } = rowStyling;
        const rowIdentifier = resolveRowId(row);
        const isSelected = isRowSelected(row);
        const {
            rowKey,
            rowRef,
            style,
            rowProps = {},
        } = options;

        return (
            <TableRow
                key={rowKey ?? rowIdentifier}
                ref={rowRef}
                style={style}
                className={classNames(
                    "group border-b border-gray-100 bg-white",
                    customRowClassName
                )}
                data-row-id={rowIdentifier}
                {...rowProps}
            >
                {row.getVisibleCells().map((cell) => {
                    const isPinned = cell.column.getIsPinned();
                    const pinnedHighlightClass = customPinnedCellClassName ?? customCellClassName;
                    const defaultPinnedBackground = isPinned && !isSelected && !pinnedHighlightClass;
                    return (
                        <TableCell
                            key={cell.id}
                            style={{...getCommonPinningStyles(cell.column)}}
                            className={classNames(
                                "align-middle p-0 text-sm text-gray-700 transition-colors",
                                customCellHoverClassName ?? "group-hover:bg-gray-50",
                                customCellClassName,
                                isPinned && !isSelected && pinnedHighlightClass,
                                defaultPinnedBackground && "bg-white",
                                isSelected && (customSelectedCellClassName ?? "bg-gray-200")
                            )}
                        >
                            {renderCellContent(cell, null)}
                        </TableCell>
                    );
                })}
            </TableRow>
        );
    };

    const renderVirtualizedRows = () => {
        if (!shouldVirtualizeRows) {
            return null;
        }
        const createPaddingRow = (height) => {
            if (!height) {
                return null;
            }
            return (
                <TableRow aria-hidden="true">
                    <TableCell
                        colSpan={visibleColumns.length || 1}
                        style={{height: `${height}px`, border: 0, padding: 0}}
                    />
                </TableRow>
            );
        };

        return (
            <TableBody>
                {createPaddingRow(virtualPaddingStart)}
                {virtualItems.map((virtualRow) => {
                    const row = rows[virtualRow.index];
                    if (!row) {
                        return null;
                    }
                    const rowKey = resolveRowId(row);
                    const rowRef = (node) => {
                        if (node) {
                            node.dataset.index = String(virtualRow.index);
                        }
                        scheduleRowMeasurement(node);
                    };
                    return renderStaticRow(row, {
                        rowKey,
                        rowRef,
                        rowProps: {"data-index": String(virtualRow.index)},
                    });
                })}
                {createPaddingRow(virtualPaddingEnd)}
            </TableBody>
        );
    };

    const renderTableRows = () => {
        if (enableReorder) {
            return (
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={handleDragStart}
                    onDragMove={handleDragMove}
                    onDragEnd={handleDragEnd}
                    onDragCancel={handleDragCancel}
                    modifiers={[restrictToVerticalAxis, restrictToFirstScrollableAncestor]}
                >
                    <SortableContext
                        items={sortableRowIds}
                        strategy={verticalListSortingStrategy}
                    >
                        <TableBody>
                            {rows.map((row) => {
                                const rowId = resolveRowId(row);
                                return <SortableTableRow key={rowId} row={row} rowId={rowId} />;
                            })}
                        </TableBody>
                    </SortableContext>
                </DndContext>
            );
        }
        if (shouldVirtualizeRows) {
            return renderVirtualizedRows();
        }
        return (
            <TableBody>
                {rows.map((row) => renderStaticRow(row))}
            </TableBody>
        );
    };

    const renderHeaderContent = useCallback((header) => {
        if (header.isPlaceholder) {
            return null;
        }

        const canSort = header.column.getCanSort();
        const sortingState = header.column.getIsSorted();
        const headerContent = flexRender(header.column.columnDef.header, header.getContext());

        if (!canSort) {
            return headerContent;
        }

        const toggleSortingHandler = header.column.getToggleSortingHandler();
        const SortingIcon = sortingState === "asc"
            ? ArrowUpIcon
            : sortingState === "desc"
                ? ArrowDownIcon
                : SwitchVerticalIcon;

        return (
            <button
                type="button"
                onClick={toggleSortingHandler}
                className="group flex w-full  items-center gap-2 uppercase text-left focus:outline-none"
            >
                <span className="flex-1 min-w-0 whitespace-normal break-words text-[11px] leading-4 group-hover:text-blue-900">
                    {headerContent}
                </span>
                <SortingIcon
                    className={classNames(
                        "h-3 w-3 flex-none text-gray-400 transition-colors",
                        sortingState && "text-blue-600"
                    )}
                    aria-hidden="true"
                />
            </button>
        );
    }, []);

    const resolveAriaSort = useCallback((header) => {
        const canSort = header.column.getCanSort();
        if (!canSort) {
            return undefined;
        }
        const sortingState = header.column.getIsSorted();
        if (sortingState === "asc") {
            return "ascending";
        }
        if (sortingState === "desc") {
            return "descending";
        }
        return "none";
    }, []);

    const hasData = rows.length > 0;
    const resolvedStickyZIndex =
        typeof stickyHeaderZIndex === "number" ? stickyHeaderZIndex : 0;
    const toolbarZIndex = resolvedStickyZIndex + 1;
    const toolbarStyle = stickyHeader && isDesktop
        ? {position: "sticky", top: stickyHeaderOffset, zIndex: toolbarZIndex}
        : undefined;
    const hideableColumns = visibleColumns.filter((column) => column.getCanHide());
    const hasVisibilityOptions = hideableColumns.length > 0;
    const hasReorderOption = enableColumnReorder && columnReorderItems.length > 1;
    const visibilityState = columnVisibility ?? {};
    const columnMenuZIndex = Math.max(resolvedStickyZIndex + 20, 100);
    const {
        hasOverflow: hasHorizontalOverflow,
        canScrollLeft: canScrollLeftHorizontally,
        canScrollRight: canScrollRightHorizontally,
    } = horizontalScrollState;

    useEffect(() => {
        if (typeof window !== "undefined" && typeof window.ResizeObserver === "function") {
            return;
        }
        updateHorizontalScrollState();
    }, [dataLength, visibleColumnSignature, updateHorizontalScrollState]);

    return (
        <>
        <div className={classNames("w-full   bg-white shadow-sm", className)}>
            <div
                ref={toolbarRef}
                className={classNames(
                    "flex flex-wrap items-center justify-between gap-3 border-b  px-4 py-3 border-t border-gray-200 bg-white",
                    stickyHeader ? "bg-white" : ""
                )}
                style={toolbarStyle}
            >
                <div className="flex  items-center ">
                    {enableSearch && typeof onSearchChange === "function" && (
                        <div className="min-w-[240px] ">
                            <InputSearch
                                onSearch={onSearchChange}
                                isDynamicSearch={false}
                                hasMargin={false}
                                length={1}
                                cleanSearch={searchResetToken}
                                extraClass="border border-gray-200 shadow-sm"
                                defaultValue={searchValue ?? ""}
                                placeholder={searchPlaceholder || t("app.common.search_placeholder", {defaultValue: "Search"})}
                            />
                        </div>
                    )}
                </div>

                <div className="flex items-center gap-2">
                    {toolbarActions}
                    {enableColumnVisibility && (
                        <div
                            ref={columnMenuRef}
                            className="relative"
                            style={{zIndex: 50}}
                        >
                            <button
                                type="button"
                                onClick={() => setShowColumnMenu((prev) => !prev)}
                                className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 hover:bg-gray-100"
                                aria-label={t("app.common.columns", {defaultValue: "Colonne"})}
                                title={t("app.common.columns", {defaultValue: "Colonne"})}
                            >
                                <ViewListIcon className="h-4 w-4" />
                            </button>
                            {showColumnMenu && (
                                <div
                                    className="absolute right-0 mt-2 w-56 rounded-md border border-gray-200 bg-white shadow-lg"
                                    style={{zIndex: columnMenuZIndex}}
                                >
                                    <div className="flex items-center justify-between px-3 py-2">
                                        <span className="text-sm font-medium text-gray-700">
                                            {t("app.common.visible_columns", {defaultValue: "Visible columns"})}
                                        </span>
                                        <button
                                            type="button"
                                            className="text-gray-400 hover:text-gray-600"
                                            onClick={() => setShowColumnMenu(false)}
                                            aria-label={t("app.common.close", {defaultValue: "Close"})}
                                        >
                                            <XIcon className="h-4 w-4" />
                                        </button>
                                    </div>
                                    {(!hasVisibilityOptions && !hasReorderOption) ? (
                                        <div className="px-3 py-2 text-sm text-gray-500">
                                            {t("app.common.no_columns_available", {defaultValue: "Non ci sono colonne da ordinare."})}
                                        </div>
                                    ) : (
                                        <>
                                            {hasVisibilityOptions && (
                                                <div className="max-h-64 overflow-auto py-2 text-sm text-gray-700">
                                                    {hideableColumns.map((column) => {
                                                        const columnId = String(column.id);
                                                        const isChecked = Object.prototype.hasOwnProperty.call(visibilityState, columnId)
                                                            ? visibilityState[columnId] !== false
                                                            : column.getIsVisible();
                                                        return (
                                                            <label
                                                                key={column.id}
                                                                className="flex cursor-pointer items-center gap-2 px-3 py-2 hover:bg-gray-50"
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isChecked}
                                                                    onChange={(event) => column.toggleVisibility(event.target.checked)}
                                                                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                                                />
                                                                <span>{resolveColumnLabel(column)}</span>
                                                            </label>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                            {hasReorderOption && (
                                                <div className={classNames("border-t border-gray-200 px-3 py-2", !hasVisibilityOptions && "pt-3")}>
                                                    <button
                                                        type="button"
                                                        onClick={handleOpenColumnReorder}
                                                        className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm font-medium text-blue-600 hover:bg-gray-50"
                                                    >
                                                        <SwitchVerticalIcon className="h-4 w-4" />
                                                        {t("app.common.reorder_columns", {defaultValue: "Riordina colonne"})}
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {typeof onAddRow === "function" && (
                        <button
                            type="button"
                            onClick={onAddRow}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-blue-1000 bg-blue-1000 text-white hover:bg-blue-900"
                            aria-label={addButtonLabel || t("app.common.add", {defaultValue: "Aggiungi"})}
                            title={addButtonLabel || t("app.common.add", {defaultValue: "Aggiungi"})}
                        >
                            <PlusIcon className="h-4 w-4" />
                        </button>
                    )}
                </div>
            </div>

            <div className="relative">
                <div
                    ref={scrollContainerRef}
                    data-table-scroll-container="true"
                    className={classNames(
                        fixedHeight || "",
                        "w-full max-w-full",
                        enableScroll ? "overflow-auto" : "overflow-x-auto overflow-y-visible"
                    )}
                >
                    <Table
                        className="min-w-full"
                        style={{width: table.getTotalSize() || "100%"}}
                    >
                        <PrimitiveTableHeader
                            style={{position: "sticky", top: 0, zIndex: stickyHeaderZIndex}}
                        >
                            {headerGroups.map((headerGroup) => (
                                <TableRow key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => {
                                        const ariaSort = resolveAriaSort(header);
                                        const isPinnedColumn = Boolean(header.column.getIsPinned());
                                        const allowPinnedResize = Boolean(header.column.columnDef?.meta?.allowPinnedResize);
                                        const canResizeColumn =
                                            typeof header.column.getCanResize === "function"
                                                ? header.column.getCanResize() && (!isPinnedColumn || allowPinnedResize)
                                                : (!isPinnedColumn || allowPinnedResize);
                                        const handleResize =
                                            canResizeColumn && typeof header.getResizeHandler === "function"
                                                ? header.getResizeHandler()
                                                : undefined;
                                        return (
                                            <TableHead
                                                key={header.id}
                                                style={{
                                                    ...getCommonPinningStyles(header.column, {isHeader: true}),
                                                    whiteSpace: "normal",
                                                }}
                                                className="sticky top-0  border-b border-gray-200 bg-solitude-400 text-left  font-semibold uppercase  text-blue-1000"
                                                aria-sort={ariaSort}
                                            >
                                                {renderHeaderContent(header)}
                                                {canResizeColumn && (
                                                    <div
                                                        onDoubleClick={() => header.column.resetSize()}
                                                        onMouseDown={handleResize}
                                                        onTouchStart={handleResize}
                                                        className={classNames(
                                                            "resizer",
                                                            table.options.columnResizeDirection,
                                                            header.column.getIsResizing() ? "isResizing" : ""
                                                        )}
                                                        role="presentation"
                                                    />
                                                )}
                                            </TableHead>
                                        );
                                    })}
                                </TableRow>
                            ))}
                        </PrimitiveTableHeader>

                        {isLoading ? (
                            <TableBody>
                                <TableRow>
                                    <TableCell colSpan={visibleColumns.length} className="py-10 text-center">
                                        <Loader />
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        ) : hasData ? (
                            renderTableRows()
                        ) : (
                            <TableBody>
                                <TableRow>
                                    <TableCell colSpan={visibleColumns.length} className="py-10">
                                        <EmptyState
                                            isActive
                                            type={NO_DATA}
                                            title={emptyStateTitle || t("app.common.no_data", {defaultValue: "No data"})}
                                            description={emptyStateDescription}
                                            className="bg-transparent"
                                        />
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        )}
                    </Table>
                </div>
                {hasHorizontalOverflow && (
                    <>
                        <div
                            aria-hidden="true"
                            className={classNames(
                                "pointer-events-none absolute top-2 bottom-2 left-0 w-16 bg-gradient-to-r from-white via-white/95 to-transparent transition-opacity duration-150",
                                canScrollLeftHorizontally ? "opacity-100" : "opacity-0"
                            )}
                        />
                        <div
                            aria-hidden="true"
                            className={classNames(
                                "pointer-events-none absolute top-2 bottom-2 right-0 w-16 bg-gradient-to-l from-white via-white/95 to-transparent transition-opacity duration-150",
                                canScrollRightHorizontally ? "opacity-100" : "opacity-0"
                            )}
                        />
                        <button
                            type="button"
                            onClick={() => handleHorizontalScroll(-1)}
                            disabled={!canScrollLeftHorizontally}
                            className={classNames(
                                "pointer-events-auto absolute left-5 top-1/2 z-20 -translate-y-1/2 transform flex h-11 w-11 items-center justify-center rounded-full border border-gray-300 bg-white/95 text-blue-900 shadow-lg transition-all duration-150 hover:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500",
                                canScrollLeftHorizontally ? "opacity-100" : "pointer-events-none opacity-0"
                            )}
                            aria-label={t("app.common.scroll_left", {defaultValue: "Scorri a sinistra"})}
                            title={t("app.common.scroll_left", {defaultValue: "Scorri a sinistra"})}
                        >
                            <ChevronLeftIcon className="h-5 w-5" />
                        </button>
                        <button
                            type="button"
                            onClick={() => handleHorizontalScroll(1)}
                            disabled={!canScrollRightHorizontally}
                            className={classNames(
                                "pointer-events-auto absolute right-5 top-1/2 z-20 -translate-y-1/2 transform flex h-11 w-11 items-center justify-center rounded-full border border-gray-300 bg-white/95 text-blue-900 shadow-lg transition-all duration-150 hover:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500",
                                canScrollRightHorizontally ? "opacity-100" : "pointer-events-none opacity-0"
                            )}
                            aria-label={t("app.common.scroll_right", {defaultValue: "Scorri a destra"})}
                            title={t("app.common.scroll_right", {defaultValue: "Scorri a destra"})}
                        >
                            <ChevronRightIcon className="h-5 w-5" />
                        </button>
                    </>
                )}
            </div>
        </div>
        {enableColumnReorder && (
            <FormPopup
                title={t("app.common.reorder_columns", {defaultValue: "Riordina colonne"})}
                isOpen={isColumnReorderOpen}
                setIsOpen={setIsColumnReorderOpen}
                defaultWith="max-w-lg"
            >
                {columnReorderItems.length > 0 ? (
                    <div className="space-y-4">
                        <p className="text-sm text-gray-500">
                            {t("app.common.reorder_columns_hint", {
                                defaultValue: "Trascina le colonne per cambiare l'ordine. Le modifiche vengono salvate automaticamente.",
                            })}
                        </p>
                        <DragDropContext onDragEnd={handleColumnReorderDragEnd}>
                            <Droppable droppableId="editable-column-reorder-list">
                                {(dropProvided) => (
                                    <div className="max-h-64 overflow-y-auto pr-1">
                                        <ul
                                            ref={dropProvided.innerRef}
                                            {...dropProvided.droppableProps}
                                            className="space-y-2"
                                        >
                                            {columnReorderItems.map((item, index) => (
                                                <Draggable key={item.id} draggableId={String(item.id)} index={index}>
                                                    {(dragProvided, dragSnapshot) => (
                                                        <li
                                                            ref={dragProvided.innerRef}
                                                            {...dragProvided.draggableProps}
                                                            style={{
                                                                ...dragProvided.draggableProps.style,
                                                            }}
                                                            className={classNames(
                                                                "flex items-center justify-between rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700",
                                                                dragSnapshot.isDragging && "shadow-md ring-1 ring-blue-200"
                                                            )}
                                                        >
                                                            <span className="flex items-center gap-3">
                                                                <span
                                                                    {...dragProvided.dragHandleProps}
                                                                    className="flex h-8 w-8 items-center justify-center rounded-md text-gray-400 hover:text-gray-600"
                                                                >
                                                                    <DragIcon className="h-4 w-4" />
                                                                </span>
                                                                <span className="font-medium text-gray-800">{item.label}</span>
                                                            </span>
                                                            {!item.isVisible && (
                                                                <span className="text-xs text-gray-400">
                                                                    {t("app.common.hidden", {defaultValue: "Nascosta"})}
                                                                </span>
                                                            )}
                                                        </li>
                                                    )}
                                                </Draggable>
                                            ))}
                                            {dropProvided.placeholder}
                                        </ul>
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                    </div>
                ) : (
                    <p className="text-sm text-gray-500">
                        {t("app.common.no_columns_available", {defaultValue: "Non ci sono colonne da ordinare."})}
                    </p>
                )}
            </FormPopup>
        )}
        </>
    );
};

DataTableEditable.propTypes = {
    listKey: PropTypes.string,
    columns: PropTypes.arrayOf(PropTypes.object).isRequired,
    data: PropTypes.arrayOf(PropTypes.object),
    isLoading: PropTypes.bool,
    searchValue: PropTypes.string,
    onSearchChange: PropTypes.func,
    searchResetToken: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.object,
    ]),
    searchPlaceholder: PropTypes.string,
    enableSearch: PropTypes.bool,
    enableColumnVisibility: PropTypes.bool,
    enableColumnReorder: PropTypes.bool,
    initialColumnVisibility: PropTypes.object,
    enableReorder: PropTypes.bool,
    onReorder: PropTypes.func,
    onSorting: PropTypes.func,
    onAddRow: PropTypes.func,
    addButtonLabel: PropTypes.string,
    toolbarActions: PropTypes.node,
    emptyStateTitle: PropTypes.string,
    emptyStateDescription: PropTypes.string,
    className: PropTypes.string,
    fixedHeight: PropTypes.string,
    enableScroll: PropTypes.bool,
    leftPinning: PropTypes.arrayOf(PropTypes.string),
    rightPinning: PropTypes.arrayOf(PropTypes.string),
    stickyHeader: PropTypes.bool,
    stickyHeaderOffset: PropTypes.number,
    stickyHeaderZIndex: PropTypes.number,
    isRowSelected: PropTypes.func,
    resolveRowStyling: PropTypes.func,
    virtualizeRows: PropTypes.bool,
    virtualRowEstimateSize: PropTypes.number,
    virtualOverscan: PropTypes.number,
};

DataTableEditable.defaultProps = {
    listKey: null,
    data: [],
    isLoading: false,
    searchValue: "",
    onSearchChange: undefined,
    searchResetToken: null,
    searchPlaceholder: "",
    enableSearch: false,
    enableColumnVisibility: true,
    enableColumnReorder: false,
    initialColumnVisibility: undefined,
    enableReorder: false,
    onReorder: undefined,
    onSorting: undefined,
    onAddRow: undefined,
    addButtonLabel: "",
    toolbarActions: null,
    emptyStateTitle: "",
    emptyStateDescription: "",
    className: "",
    fixedHeight: "",
    enableScroll: true,
    leftPinning: [],
    rightPinning: [],
    stickyHeader: true,
    stickyHeaderOffset: 0,
    stickyHeaderZIndex: 50,
    isRowSelected: () => false,
    resolveRowStyling: undefined,
    virtualizeRows: false,
    virtualRowEstimateSize: 64,
    virtualOverscan: 8,
};

export default DataTableEditable;
